/**
 * Phase 27.50.B — Cart page (`/cart`).
 *
 * Review cart items, edit quantities/personalization, remove items, and
 * proceed to a single multi-item checkout. All prices come from the
 * server (single source of truth) — the client only DISPLAYS them.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { shopAPI } from '@/lib/api';
import { notifyCartUpdated } from '@/hooks/useCartCount';
import { getErrorMessage } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, Trash2, Plus, Minus, ShoppingBag, ArrowLeft, Tag, X } from 'lucide-react';

export default function CartPage({ embedded = false }) {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await shopAPI.getCart();
      setCart(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load cart'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const bump = async (item, delta) => {
    const next = item.quantity + delta;
    if (next < 1) return;
    setBusy(item.item_id);
    try {
      const r = await shopAPI.updateItem(item.item_id, { quantity: next });
      setCart(r.data);
      notifyCartUpdated();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to update'));
    } finally { setBusy(null); }
  };

  const remove = async (item) => {
    setBusy(item.item_id);
    try {
      const r = await shopAPI.removeItem(item.item_id);
      setCart(r.data);
      notifyCartUpdated();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to remove'));
    } finally { setBusy(null); }
  };

  const clear = async () => {
    if (!window.confirm('Clear entire cart?')) return;
    try {
      const r = await shopAPI.clearCart();
      setCart(r.data);
      notifyCartUpdated();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed'));
    }
  };

  const checkout = () => nav('/checkout/cart');

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    try {
      const r = await shopAPI.applyCoupon({ code: couponCode });
      setCart(r.data);
      setCouponCode('');
      toast.success(`Coupon ${r.data.coupon_code} applied`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Invalid coupon'));
    } finally { setApplyingCoupon(false); }
  };

  const removeCoupon = async () => {
    try {
      const r = await shopAPI.removeCoupon();
      setCart(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed'));
    }
  };

  if (loading) return (
    <div className={`${embedded ? '' : 'max-w-3xl mx-auto p-8'} flex items-center gap-2 text-slate-500`}>
      <Loader2 size={16} className="animate-spin" /> Loading cart…
    </div>
  );

  const items = cart?.items || [];

  return (
    <div className={embedded ? '' : 'max-w-4xl mx-auto p-6'} data-testid="cart-page">
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShoppingBag size={22} /> Your Cart
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {items.length === 0
                ? 'Your cart is empty.'
                : `${items.length} product${items.length === 1 ? '' : 's'} · ${items.reduce((s, i) => s + i.quantity, 0)} unit${items.reduce((s, i) => s + i.quantity, 0) === 1 ? '' : 's'}`}
            </p>
          </div>
          <Link to="/shop">
            <Button variant="outline" size="sm" data-testid="cart-continue-shopping">
              <ArrowLeft size={14} className="mr-1.5" />
              Continue shopping
            </Button>
          </Link>
        </div>
      )}

      {items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center text-slate-500">
            <ShoppingBag className="mx-auto mb-3 text-slate-300" size={32} />
            <p>Nothing here yet.</p>
            <Link to="/shop">
              <Button className="mt-4">Browse products</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3 mb-6">
            {items.map((it) => (
              <Card key={it.item_id} data-testid={`cart-item-${it.item_id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{it.title}</p>
                    <p className="text-xs text-slate-500 font-mono">${Number(it.unit_price).toFixed(2)} each</p>
                    {it.pricing_source === 'tier' && it.matched_tier && (
                      <span className="inline-block text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 mt-1"
                            data-testid={`tier-badge-${it.item_id}`}>
                        Bulk tier · {it.matched_tier.quantity} units
                      </span>
                    )}
                    {Object.entries(it.personalization || {}).length > 0 && (
                      <p className="text-[11px] text-slate-500 mt-1">
                        {Object.entries(it.personalization).map(([k, v]) =>
                          v ? <span key={k} className="mr-2"><strong>{k}:</strong> {v}</span> : null
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 border rounded-md" data-testid={`qty-controls-${it.item_id}`}>
                    <Button size="sm" variant="ghost"
                             onClick={() => bump(it, -1)}
                             disabled={busy === it.item_id || it.quantity <= 1}>
                      <Minus size={12} />
                    </Button>
                    <span className="px-3 text-sm font-mono">{it.quantity}</span>
                    <Button size="sm" variant="ghost"
                             onClick={() => bump(it, 1)}
                             disabled={busy === it.item_id}>
                      <Plus size={12} />
                    </Button>
                  </div>
                  <div className="w-24 text-right font-semibold text-slate-900">
                    ${Number(it.line_total != null ? it.line_total : (it.unit_price * it.quantity)).toFixed(2)}
                  </div>
                  <Button size="sm" variant="ghost"
                           onClick={() => remove(it)}
                           disabled={busy === it.item_id}
                           data-testid={`cart-remove-${it.item_id}`}>
                    <Trash2 size={14} className="text-rose-500" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardContent className="p-4 space-y-3">
              {/* Coupon input */}
              {cart.coupon_code ? (
                <div className="flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2">
                  <span className="text-sm text-emerald-800 flex items-center gap-2">
                    <Tag size={13} /> Coupon <strong>{cart.coupon_code}</strong> applied
                  </span>
                  <Button variant="ghost" size="sm" onClick={removeCoupon}
                          data-testid="cart-remove-coupon">
                    <X size={13} />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2" data-testid="cart-coupon-form">
                  <Input placeholder="Coupon code" value={couponCode}
                         onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                         onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                         data-testid="cart-coupon-input" />
                  <Button variant="outline" onClick={applyCoupon}
                          disabled={applyingCoupon || !couponCode.trim()}
                          data-testid="cart-coupon-apply">
                    {applyingCoupon && <Loader2 size={13} className="mr-1 animate-spin" />}
                    Apply
                  </Button>
                </div>
              )}

              <div className="flex justify-between text-sm text-slate-600">
                <span>Subtotal</span>
                <span className="font-mono">${Number(cart.subtotal).toFixed(2)}</span>
              </div>
              {Number(cart.discount_amount || 0) > 0 && (
                <div className="flex justify-between text-sm text-emerald-700"
                     data-testid="cart-discount-row">
                  <span>Discount</span>
                  <span className="font-mono">-${Number(cart.discount_amount).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-slate-500">
                <span>Shipping</span>
                <span className="font-mono">
                  {Number(cart.shipping_amount || 0) === 0
                    ? 'Free'
                    : `$${Number(cart.shipping_amount).toFixed(2)}`}
                </span>
              </div>
              <div className="border-t pt-3 flex justify-between text-lg font-bold text-slate-900">
                <span>Total</span>
                <span data-testid="cart-total" className="font-mono">${Number(cart.total).toFixed(2)}</span>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="ghost" size="sm" onClick={clear}
                         data-testid="cart-clear">Clear cart</Button>
                <Button onClick={checkout} data-testid="cart-checkout-btn">
                  Checkout — ${Number(cart.total).toFixed(2)}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
